import { DeleteDocument } from "./DeleteDocument";
import { Archive, ArrowLeft, EyeOff, Pencil, Send, Share2, Undo2 } from "lucide-react";
import { useImperativeHandle, useRef, type ReactNode, type Ref } from "react";
import { Button } from "../components/ui/button";
import { Sheet, SheetBody, SheetFooter, SheetHeader } from "../components/ui/sheet";
import { FieldError } from "../components/ui/field";
import { ContentLoading } from "../components/ContentLoading";
import { CustomerDocumentReader } from "../knowledge/CustomerDocumentReader";
import { CustomerDocumentForm } from "./CustomerDocumentForm";
import {
  useCustomerDocumentDialog,
  type CustomerDocumentDialogProps,
  type CustomerDocumentDialogState,
} from "./use-customer-document-dialog";
function Preview({ state, slug }: { state: CustomerDocumentDialogState; slug: string }) {
  const query = state.preview;
  if (query.isPending)
    return <ContentLoading label="Loading document preview" variant="document" />;
  if (query.error)
    return (
      <div className="space-y-3">
        <FieldError>{query.error.message}</FieldError>
        <Button
          variant="outline"
          onClick={() => {
            void query.refetch();
          }}
        >
          Retry preview
        </Button>
      </div>
    );
  return (
    <CustomerDocumentReader
      document={query.data.document}
      imagePrefix={`/api/ops/customers/${slug}/document-preview/${state.document?.id}/images/`}
    />
  );
}
function sharedAudience(customerName: string, kind: CustomerDocumentDialogProps["kind"]) {
  return `${customerName}’s workspace ${kind === "knowledge" ? "members" : "admins"}`;
}
function DocumentActions({
  state,
  canWrite,
}: {
  state: CustomerDocumentDialogState;
  canWrite: boolean;
}) {
  const document = state.document;
  if (!canWrite || !document || state.previewing) return null;
  if (document.archived)
    return (
      <Button
        disabled={state.busy}
        onClick={() => {
          state.requestAction("restore");
        }}
      >
        <Undo2 />
        Restore
      </Button>
    );
  return (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          state.requestAction("archive");
        }}
        disabled={state.busy}
      >
        <Archive />
        Archive
      </Button>
      <Button
        variant="secondary"
        onClick={() => {
          state.setEditing(true);
        }}
        disabled={state.busy}
      >
        <Pencil />
        Edit details
      </Button>
    </>
  );
}

/** A pending confirmation takes over the footer: its question on the left, its answers on the right. */
function ConfirmationFooter({
  state,
  props,
}: {
  state: CustomerDocumentDialogState;
  props: CustomerDocumentDialogProps;
}) {
  if (!state.confirmation) return null;
  const audience = sharedAudience(props.customerName, props.kind);
  const messages = {
    archive: state.document?.published
      ? `Archive this document? It moves to Archived and ${audience} lose access. You can restore it anytime.`
      : "Archive this document? It moves to Archived. You can restore it anytime.",
    unpublish: `Stop sharing with ${props.customerName}? ${audience} lose access right away. Your team’s copy stays here.`,
    discard: "Discard your unsaved changes?",
    discardEdit: "Discard your unsaved changes?",
  };
  const labels = {
    archive: "Archive",
    unpublish: "Stop sharing",
    discard: "Discard changes",
    discardEdit: "Discard changes",
  };
  return (
    <SheetFooter
      start={
        <p className="m-0 max-w-72 text-sm font-semibold" role="alert">
          {messages[state.confirmation]}
        </p>
      }
    >
      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          state.setConfirmation(null);
        }}
        disabled={state.busy}
      >
        Keep working
      </Button>
      <Button type="button" variant="destructive" onClick={state.confirm} disabled={state.busy}>
        {labels[state.confirmation]}
      </Button>
    </SheetFooter>
  );
}

function sharingStatus(state: CustomerDocumentDialogState, audience: string) {
  const document = state.document;
  if (state.previewing) return `This is exactly what ${audience} will see.`;
  if (document?.archived) return "Archived. Only your team can see it, under Archived.";
  if (!document?.published) return "Only your team can see this.";
  const changed =
    JSON.stringify(document.draft.fields) !== JSON.stringify(document.published.fields);
  return changed
    ? `Shared with ${audience}. They still see the earlier version of your latest edits.`
    : `Shared with ${audience}.`;
}
function SharingPanel({
  state,
  props,
}: {
  state: CustomerDocumentDialogState;
  props: CustomerDocumentDialogProps;
}) {
  const audience = sharedAudience(props.customerName, props.kind);
  const document = state.document;
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-sm px-4 py-3 text-sm ${state.previewing ? "bg-info-background text-info-foreground" : "bg-muted text-muted-foreground"}`}
      role="status"
    >
      <p>{(!state.previewing && state.notice) || sharingStatus(state, audience)}</p>
      {props.canWrite && document && !document.archived && !state.confirmation ? (
        <SharingActions state={state} customerName={props.customerName} />
      ) : null}
    </div>
  );
}
function SharingActions({
  state,
  customerName,
}: {
  state: CustomerDocumentDialogState;
  customerName: string;
}) {
  const document = state.document;
  const ready = !state.busy && state.preview.isSuccess && !state.preview.isFetching;
  if (state.previewing) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {document?.published ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={state.busy}
          onClick={() => {
            state.requestAction("unpublish");
          }}
        >
          <EyeOff />
          Stop sharing
        </Button>
      ) : null}
      <Button
        variant="outline"
        size="sm"
        disabled={!ready}
        onClick={() => {
          state.setPreviewing(true);
        }}
      >
        <Share2 />
        {document?.published ? "Update shared copy…" : `Share with ${customerName}…`}
      </Button>
    </div>
  );
}
/** While previewing the customer's copy, the footer confirms or cancels sharing. */
function ShareFooter({
  state,
  customerName,
}: {
  state: CustomerDocumentDialogState;
  customerName: string;
}) {
  const ready = !state.busy && state.preview.isSuccess && !state.preview.isFetching;
  return (
    <SheetFooter>
      <Button
        variant="ghost"
        disabled={state.busy}
        onClick={() => {
          state.setPreviewing(false);
        }}
      >
        Cancel
      </Button>
      <Button
        disabled={!ready}
        onClick={() => {
          state.requestAction("publish");
        }}
      >
        <Send />
        {state.busy
          ? "Sharing…"
          : state.document?.published
            ? "Share latest version"
            : `Share with ${customerName}`}
      </Button>
    </SheetFooter>
  );
}

const kindLabel = { knowledge: "Knowledge", agreement: "Agreement", quote: "Quote document" };

export type DocumentPanelHandle = { close: () => void };
type PanelProps = CustomerDocumentDialogProps & {
  /** Returns to the content this panel replaced, such as the contract the signed copy came from. */
  back?: { label: string; onBack: () => void };
  /** Facts shown above the document, such as the tool it belongs to. */
  aside?: ReactNode;
  ref?: Ref<DocumentPanelHandle>;
};

function panelTitle(state: CustomerDocumentDialogState, props: CustomerDocumentDialogProps) {
  if (state.editing)
    return state.document
      ? "Edit details"
      : `Add ${props.kind === "knowledge" ? "document" : props.kind}`;
  if (state.previewing) return `Share with ${props.customerName}`;
  return state.document?.draft.fields.title ?? "Document";
}

/**
 * One customer document as side-sheet content (ADR-0087): reading, sharing, and editing all
 * happen in the same sheet. Its header, body, and footer change in place.
 */
export function CustomerDocumentPanel({ back, aside, ref, ...props }: PanelProps) {
  const state = useCustomerDocumentDialog(props);
  useImperativeHandle(ref, () => ({ close: state.close }), [state.close]);
  const confirmation = state.confirmation ? (
    <ConfirmationFooter state={state} props={props} />
  ) : null;
  return (
    <>
      <SheetHeader
        title={panelTitle(state, props)}
        kicker={`${props.customerName} · ${kindLabel[props.kind]}`}
      />
      {state.editing ? (
        <CustomerDocumentForm
          slug={props.slug}
          kind={props.kind}
          {...(state.document ? { current: state.document } : {})}
          canUsePages={props.canUsePages}
          onSaved={(value) => {
            void state.saved(value);
          }}
          onCancel={state.cancelEdit}
          onDirty={state.setDirty}
          onBusy={state.setFormBusy}
          footer={confirmation}
        />
      ) : (
        <>
          <SheetBody>
            {state.previewing ? null : aside}
            <SharingPanel state={state} props={props} />
            <Preview state={state} slug={props.slug} />
            <DocumentDeleteAction state={state} props={props} hasBack={Boolean(back)} />
            {state.action.error ? <FieldError>{state.action.error.message}</FieldError> : null}
          </SheetBody>
          {confirmation ??
            (state.previewing ? (
              <ShareFooter state={state} customerName={props.customerName} />
            ) : (
              <SheetFooter
                start={
                  <Button
                    variant="ghost"
                    onClick={back ? back.onBack : state.close}
                    disabled={state.busy}
                  >
                    <ArrowLeft />
                    {back ? back.label : "Back to documents"}
                  </Button>
                }
              >
                <DocumentActions state={state} canWrite={props.canWrite} />
              </SheetFooter>
            ))}
        </>
      )}
    </>
  );
}

/** A customer document in its own side sheet, for pages that are not already showing one. */
export function CustomerDocumentDialog(props: CustomerDocumentDialogProps) {
  const panel = useRef<DocumentPanelHandle>(null);
  return (
    <Sheet
      open
      onClose={() => {
        panel.current?.close();
      }}
    >
      <CustomerDocumentPanel ref={panel} {...props} />
    </Sheet>
  );
}

function DocumentDeleteAction({
  state,
  props,
  hasBack,
}: {
  state: CustomerDocumentDialogState;
  props: CustomerDocumentDialogProps;
  hasBack: boolean;
}) {
  if (!props.canWrite || !state.document || state.previewing || hasBack) return null;
  return (
    <DeleteDocument
      slug={props.slug}
      source="library"
      id={state.document.id}
      revision={state.document.revision}
      title={state.document.draft.fields.title}
      disabled={state.busy}
      onDeleted={props.onClose}
    />
  );
}
